import {
  SmsPackage,
  SmsPurchaseRequest,
  SmsCheckoutResponse,
  SmsSettings,
  SmsPurchaseRecord,
} from "../../types/sms.types";
import apiService from "../api/api.service";

class SmsService {
  private static instance: SmsService;

  public static getInstance(): SmsService {
    if (!SmsService.instance) {
      SmsService.instance = new SmsService();
    }
    return SmsService.instance;
  }

  // Private constructor
  private constructor() {}

  /**
   * Tüm aktif SMS paketlerini listeler
   * @returns Aktif SMS paketleri listesi
   */
  public async listPackages(): Promise<SmsPackage[]> {
    try {
      const response = await apiService.get<SmsPackage[]>("/sms/packages");
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * SMS paketi satın alma işlemini başlatır
   * @param purchaseData SMS satın alma verisi
   * @returns Checkout formu ve token bilgileri
   */
  public async purchasePackage(
    purchaseData: SmsPurchaseRequest
  ): Promise<SmsCheckoutResponse> {
    try {
      const response = await apiService.post<SmsCheckoutResponse>(
        "/sms/purchase",
        purchaseData
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Şirketin SMS ayarlarını getirir
   * @param companyId Şirket ID
   * @returns SMS ayarları
   */
  public async getSmsSettings(companyId: number): Promise<SmsSettings> {
    try {
      const response = await apiService.get<SmsSettings>(
        `/companies/${companyId}/sms-settings`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Şirketin SMS satın alma geçmişini getirir
   * @param companyId Şirket ID
   * @returns SMS satın alma kayıtları
   */
  public async getSmsPurchaseHistory(
    companyId: number
  ): Promise<SmsPurchaseRecord[]> {
    try {
      const response = await apiService.get<SmsPurchaseRecord[]>(
        `/companies/${companyId}/sms-history`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * SMS bakiyesini günceller (admin paneli için)
   * @param companyId Şirket ID
   * @param smsLimit Yeni SMS limiti
   * @returns Güncellenmiş SMS ayarları
   */
  public async updateSmsLimit(
    companyId: number,
    smsLimit: number
  ): Promise<SmsSettings> {
    try {
      const response = await apiService.put<SmsSettings>(
        `/companies/${companyId}/sms-limit`,
        { smsLimit }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * SMS ödeme doğrulama işlemi - Yeni API dokümantasyonuna uygun
   * @param token Ödeme token'ı (URL'den gelen)
   * @param conversationId Konuşma ID'si (localStorage'dan gelen)
   * @returns Ödeme doğrulama sonucu
   */
  public async verifyPurchase(
    token: string,
    conversationId: string
  ): Promise<{
    message: string;
    newSmsLimit: number;
    purchasedPackage: {
      name: string;
      smsAmount: number;
      price: string;
    };
  }> {
    try {
      const response = await apiService.post<{
        message: string;
        newSmsLimit: number;
        purchasedPackage: {
          name: string;
          smsAmount: number;
          price: string;
        };
      }>("/sms/verify-purchase", { 
        token,
        conversationId 
      });


      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * SMS otomatik doldurma ayarlarını günceller
   * @param companyId Şirket ID
   * @param autoRechargeSettings Otomatik doldurma ayarları
   * @returns Güncellenmiş SMS ayarları
   */
  public async updateAutoRechargeSettings(
    companyId: number,
    autoRechargeSettings: {
      autoRecharge: boolean;
      autoRechargeThreshold?: number;
      autoRechargePackageId?: number;
    }
  ): Promise<SmsSettings> {
    try {
      const response = await apiService.put<SmsSettings>(
        `/companies/${companyId}/sms-auto-recharge`,
        autoRechargeSettings
      );
      return response;
    } catch (error) {
      
      throw error;
    }
  }

}

// Singleton instance export
const smsService = SmsService.getInstance();
export default smsService;
